import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenant } from '../../hooks/useTenant';
import { SocialLoginButton } from '../../components/auth/SocialLoginButton';
import { Loading } from '../../components/common/Loading';
import { Card } from '../../components/common/Card';
import { initGoogleAuth, initFacebookAuth } from '../../api/auth';
import { ROUTES } from '../../utils/constants';

export function Login() {
  const { tenant, config, isLoading, error } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const from = (location.state as { from?: Location })?.from?.pathname || ROUTES.home(tenant);

  const handleGoogleLogin = async () => {
    if (!tenant) return;
    setIsGoogleLoading(true);
    setAuthError(null);

    try {
      const { authUrl } = await initGoogleAuth(tenant, from);
      window.location.href = authUrl;
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Failed to initiate login');
      setIsGoogleLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    if (!tenant) return;
    setIsFacebookLoading(true);
    setAuthError(null);

    try {
      const { authUrl } = await initFacebookAuth(tenant, from);
      window.location.href = authUrl;
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Failed to initiate login');
      setIsFacebookLoading(false);
    }
  };

  if (isLoading) {
    return <Loading fullScreen message="Loading..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Oops!</h1>
          <p className="text-gray-600">{error}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          {config?.branding.logoUrl ? (
            <img
              src={config.branding.logoUrl}
              alt={config.name}
              className="h-12 mx-auto mb-4"
            />
          ) : (
            <h1 className="text-3xl font-bold text-primary-600 mb-2">
              {config?.name || 'OnlyCoach'}
            </h1>
          )}
          {config?.branding.tagline && (
            <p className="text-gray-600">{config.branding.tagline}</p>
          )}
        </div>

        <Card>
          <h2 className="text-xl font-semibold text-gray-900 text-center mb-6">
            Sign in to continue
          </h2>

          {authError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {authError}
            </div>
          )}

          <div className="space-y-3">
            {config?.features.googleAuth && (
              <SocialLoginButton
                provider="google"
                onClick={handleGoogleLogin}
                isLoading={isGoogleLoading}
                disabled={isFacebookLoading}
              />
            )}

            {config?.features.facebookAuth && (
              <SocialLoginButton
                provider="facebook"
                onClick={handleFacebookLogin}
                isLoading={isFacebookLoading}
                disabled={isGoogleLoading}
              />
            )}
          </div>

          <p className="mt-6 text-center text-xs text-gray-500">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </Card>
      </div>
    </div>
  );
}
